import React, { useState, useEffect, memo, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useSelector, useDispatch } from 'react-redux';
import { CardHeader, Button, Form, Row, Col } from 'reactstrap';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import ExperimentAnnotationsModal from './experimentAnnotationsModal';
import { withSendRequest } from '../../main/hoc/withSendRequest';
import { getExperimentIdsFields } from '../actions/experimentOverview';
import { ErrorBoundary } from '../../main/hoc/errorboundary';
import { formatDate } from '../utils';
import * as notify from '../../main/utils/notify';

const ExperimentViewHeader = ({ experimentId, sendRequest }) => {
  const selectVariableRef = useRef(null);
  
  const [ annotationsModalOpen, setAnnotationsModal ] = useState(false);
  const [ annotationsCount, setAnnotationsCount ] = useState(0);

  const lastSearch = useSelector((state) => state.experiments.get('lastSearch'));
  const currentPage = useSelector((state) => state.experiments.get('pagination').get('currentPage'));
  const experimentInfo = useSelector((state) => state.experimentOverview.get('experimentInfo').get('data'));
  const { pulse, run, machine, date } = experimentInfo;
  const experimentIdsFields = useSelector((state) => state.experimentOverview.get('experimentIdsFields').get('data'));
  const experimentIdsFieldsLoading = useSelector((state) => state.experimentOverview.get('experimentIdsFields').get('loading'));

  const { t } = useTranslation();

  const history = useHistory();

  const dispatch = useDispatch();

  useEffect(() => { 
    if (!experimentIdsFields || experimentId !== experimentIdsFields.experimentId) {
      if (!experimentIdsFieldsLoading) {
        dispatch(getExperimentIdsFields({ sendRequest, experimentId }));
      }
    }
  }, [ dispatch, sendRequest, experimentId, experimentIdsFields, experimentIdsFieldsLoading ]);

  useEffect(() => {
    const fetchAnnotations = async () => {
      try {
        const response = await sendRequest('get', `experiments/${experimentId}/annotations`);
        setAnnotationsCount(response.data.length);
      }
      catch (err) {
        notify.error(t('annotations_modal.fetching_data_error'));
      }
    };
    fetchAnnotations();
  }, [ annotationsModalOpen, sendRequest, experimentId, t ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const chosenVariable = selectVariableRef.current?.select?.getValue()[0];
    const chosenIdsField = chosenVariable?.value?.ids_field;
    if (chosenIdsField) {
      history.push(`/experiments/${experimentId}?name=${chosenIdsField}`);
    }
    else {
      history.push(`/experiments/${experimentId}`);
    }
  };

  const backToListLink = () => {
    if (lastSearch) {
      return `/experiments${lastSearch}${currentPage ? `&page=${currentPage}` : ''}`;
    }
    else {
      return `/experiments${currentPage ? `?page=${currentPage}` : ''}`;
    }
  };

  const toggleAnnotationsModal = () => setAnnotationsModal((prev) => !prev);

  return (
    <>
      { annotationsModalOpen && <ExperimentAnnotationsModal
        experimentId={Number(experimentId)}
        isOpen={annotationsModalOpen}
        toggle={toggleAnnotationsModal}
      /> }
      <CardHeader>
        <Row className='align-items-center'>
          <Col>
            <span>{t('shot_label')}:{' '}{pulse}</span>
            <span></span>
          </Col>
          <Col>
            <span>{t('run_label')}:{' '}{run}</span>
            <span></span>
          </Col>
          <Col>
            <span>{t('machine_label')}:{' '}{machine} </span>
            <span></span>
          </Col>
          <Col>
            <span>{t('date_label')}:{' '}{formatDate(date)} </span>
            <span></span>
          </Col>
          <Col md='1'>
            <div className='d-flex align-items-center'>
              <Link
                to={backToListLink}
                className='btn-bg-image btn-close mr-2'
                title={t('experiment_view_header.back_to_experiments_list')}
              >
              </Link>
              <Button
                onClick={toggleAnnotationsModal}
                color='link'
                className={ 'btn-bg-image btn-annotate' + (annotationsCount ? '-green' : '') }
                title={t('open_shot_annotations_label') + (annotationsCount ? ` (${annotationsCount})` : '') }
              >
              </Button>
            </div>
          </Col>
          <Col md='4'>
            <Form onSubmit={handleSubmit}>
              <div className='d-flex'>
                <Select ref={selectVariableRef} className="ac-select-container"
                  classNamePrefix="ac-select" isSearchable={true} isClearable={true}
                  options={ experimentIdsFields?.items?.map(
                    (variable) => { return { "value": variable, "label": variable.ids_field };})} />

                <Button color='primary' className='experiment-view-search-btn' id='search-button'>
                  {t('experiment_view_header.search_button_text')}
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </CardHeader>
    </>
  );
};

ExperimentViewHeader.propTypes = {
  experimentId: PropTypes.string,
  sendRequest: PropTypes.func.isRequired, // HOC
};

export default memo(compose(
  withSendRequest,
  ErrorBoundary((props) => props.t('components.experiment_view_header'))
)(ExperimentViewHeader));
